import { RouteElement, RouteProtectedElement } from '@components'

import { Navigate, Route, Routes } from 'react-router'

import { useIsEInvoicingOnly } from '@features/bills/hooks/useIsEInvoicingOnly'
import { useIsDemoInvoiceEditorEnabled } from '@features/demoInvoice/hooks/useIsDemoInvoiceEditorEnabled'
import { EInvoicingSetup } from '@features/eInvoicing/routes'
import { useExpertReportsAvailability } from '@features/expertReports'
import { useIsQuickStartEligible } from '@features/quickStart/hooks/useIsQuickStartEligible'
import { useIsFrenchUmbrellaOrganization } from '@features/transactions/hooks/useIsFrenchUmbrellaOrganization'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { Account } from '@views/account'
import { BankAccounts } from '@views/bankAccounts/routes/BankAccounts'
import { BankConnectionEmpty } from '@views/bankAccounts/routes/BankConnectionEmpty'
import { BankTransactions } from '@views/bankTransactions/routes/BankTransactions'
import { useExpenseRevampFeatureFlags } from '@views/bills/hooks/useExpenseRevampFeatureFlags'
import { BillsSettings } from '@views/bills/routes/BillsSettings'
import { CashPilotView } from '@views/cashPilot'
import { DemoInvoice } from '@views/demoInvoice'
import { HelpCenterPage } from '@views/helpCenter'
import { SettingsRoute } from '@views/settings/enums/settingsRoute'

import { LabsFlag } from '../../enums/labsFlag'
import { Scope } from '../../enums/scope'
import { useOrganizationSource } from '../../hooks'
import { useLabsMenuFlags } from '../../hooks/useLabsMenuFlags'
import { useOrgUsesReceiptsOnly } from '../../hooks/useOrgUsesReceiptsOnly'
import { getAccessToken } from '../../utils'
import { isAuthorized } from '../../utils/isAuthorized'
import { getOrganizationViewUrl } from '../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../utils/routing/reactRoute'
import { BillEdit } from '../bills/routes/BillEdit'
import { BillsList } from '../bills/routes/BillsList'
import { BillsListEmpty } from '../bills/routes/BillsListEmpty'
import { BillView } from '../bills/routes/BillView'
import { ClientDocuments } from '../clientDocuments'
import { ClientsEmptyState, ClientsList } from '../clients'
import { ContactDetail } from '../contacts/elements/ContactDetail'
import { CreditorsList } from '../creditors'
import { CustomersBalanceOverview } from '../customersBalance'
import { CustomersBalanceDetailView } from '../customersBalance/routes'
import { Dashboard } from '../dashboard'
import { DebtorsList } from '../debtors/routes/DebtorsList'
import { Debug } from '../debug'
import { useHasDeliveryNotes } from '../deliveryNotes/hooks/useHasDeliveryNotes'
import { DeliveryNotes } from '../deliveryNotes/routes/DeliveryNotes'
import { useIsDmsEnabled } from '../documents/hooks/useIsDmsEnabled'
import { ExpertBalanceSheet } from '../expertBalanceSheet'
import { ExpertDocuments } from '../expertDocuments'
import { ExpertProfitAndLoss } from '../expertProfitAndLoss'
import { ExpertTrialBalance } from '../expertTrialBalance'
import { Exports } from '../exports'
import { getInvoiceEditorRoutePaths, InvoiceEditorRoute } from '../externalInvoices/routes/InvoiceEditor'
import { InvoicesList as ExternalInvoicesList } from '../externalInvoices/routes/InvoicesList'
import { InvoiceView } from '../externalInvoices/routes/InvoiceView'
import { FindAccountant } from '../findAccountant'
import { ImportedInvoices } from '../importedInvoices'
import { InvoicesList } from '../invoices/routes/InvoicesList'
import { InvoicesListEmpty } from '../invoices/routes/InvoicesListEmpty'
import { QuotesList as QuotesListLegacy } from '../legacyQuotes/routes/QuotesList'
import { QuotesListEmpty as QuotesListEmptyLegacy } from '../legacyQuotes/routes/QuotesListEmpty'
import { MyClient } from '../myClient'
import { OnboardingSetup } from '../onboarding/routes'
import { Products } from '../products/routes/Products'
import { QuickStart } from '../quickStart'
import { QuotesList } from '../quotes/routes/QuotesList/'
import { QuoteView } from '../quotes/routes/QuoteView/'
import { ReceiptsList } from '../receipts/routes'
import { RecurringInvoicesList } from '../recurringInvoices/routes/RecurringInvoicesList'
import { RecurringInvoiceView } from '../recurringInvoices/routes/RecurringInvoiceView'
import { Reports } from '../reports'
import { ReviewEntries } from '../reviewEntries'
import { SalaryIntegration } from '../salaryIntegration'
import { getSalesReportRoutePaths, SalesReportRoute } from '../salesReport'
import { SalesReportEmpty } from '../salesReport/routes/SalesReportEmpty'
import { SupplierDetail, SuppliersEmptyState, SuppliersList } from '../suppliers'
import { SuppliersBalanceOverview } from '../suppliersBalance'
import { SuppliersBalanceDetailView } from '../suppliersBalance/routes'
import { ThingsToDo } from '../thingsToDo'
import { Transactions } from '../transactions'
import { VatDeclarations } from '../vatDeclarations'
import { SalesTax } from '../vatDeclarations/elements/SalesTax'
import { VatDeclaration } from '../vatDeclarations/elements/VatDeclaration'
import { VatSuggestions } from '../vatDeclarations/elements/VatSuggestions'
import { useOnboardingLoginRedirect } from './hooks/useOnboardingLoginRedirect'
import { useRedirectHashUrls } from './hooks/useRedirectHashUrls'
import * as Styled from './styles'

export const ViewContainer = () => {
  const { organization } = useUserOrganization()
  useRedirectHashUrls()
  const { isOnboardingRedirectPending } = useOnboardingLoginRedirect()
  const { isSourceZervant, isSourceBilly } = useOrganizationSource()
  const hasDeliveryNotes = useHasDeliveryNotes()
  const isEligibleForQuickStart = useIsQuickStartEligible()
  const isDemoInvoiceEditorEnabled = useIsDemoInvoiceEditorEnabled()
  const accessToken = getAccessToken()
  const labsFlags = useLabsMenuFlags()
  const isMyClientEnabled = labsFlags[LabsFlag.NavigationMyClient]
  const {
    isBalanceSheetEnabled,
    isCustomersBalanceEnabled,
    isProfitAndLossEnabled,
    isSuppliersBalanceEnabled,
    isTrialBalanceEnabled,
    isVatDeclarationsEnabled,
  } = useExpertReportsAvailability()
  const isDmsEnabled = useIsDmsEnabled()
  const isEInvoicingOnly = useIsEInvoicingOnly()
  const orgUsesReceiptsOnly = useOrgUsesReceiptsOnly()
  const isFrenchUmbrellaOrganization = useIsFrenchUmbrellaOrganization()
  const { areFlagsLoading: areExpenseFlagsLoading, isRevampedPurchaseListEnabled } = useExpenseRevampFeatureFlags()

  if (!organization || isOnboardingRedirectPending) {
    return null
  }

  const uploadsUrl = getOrganizationViewUrl(organization.url, reactRoute.organization.uploads.getMainRoute())
  const suppliersUrl = getOrganizationViewUrl(organization.url, reactRoute.organization.suppliers.getMainRoute())
  const supplierDetailElement = (() => {
    if (orgUsesReceiptsOnly) {
      return <Navigate to={uploadsUrl} replace />
    }

    if (areExpenseFlagsLoading) {
      return null
    }

    return isRevampedPurchaseListEnabled ? <SupplierDetail /> : <Navigate to={suppliersUrl} replace />
  })()

  return (
    <Styled.DrawerBackgroundWrapper>
      <Styled.ContentArea>
        <Routes>
          {/* Redirect to Dashboard from main organization page */}
          <Route
            path={getOrganizationViewUrl(organization.url, '')}
            element={
              <RouteElement>
                <Navigate
                  to={getOrganizationViewUrl(
                    organization.url,
                    isEInvoicingOnly
                      ? reactRoute.organization.uploads.getMainRoute()
                      : reactRoute.organization.dashboard.getMainRoute(),
                  )}
                  replace
                />
              </RouteElement>
            }
          />

          {/* Account */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.accounts.getAccountRoute())}
            element={
              <RouteElement>
                <Account />
              </RouteElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.chartOfAccounts.getMainRoute())}
            element={
              <RouteElement>
                <Navigate
                  to={getOrganizationViewUrl(organization.url, 'settings', {
                    initialRoute: SettingsRoute.OrganizationAccountingChartOfAccounts,
                  })}
                  replace
                />
              </RouteElement>
            }
          />

          {/* Dashboard */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.dashboard.getMainRoute())}
            element={
              <RouteElement>
                {isEInvoicingOnly ? (
                  <Navigate
                    to={getOrganizationViewUrl(organization.url, reactRoute.organization.uploads.getMainRoute())}
                    replace
                  />
                ) : (
                  <Dashboard />
                )}
              </RouteElement>
            }
          />

          {/* My Client */}
          {isMyClientEnabled && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.myClient.getMainRoute())}
              element={
                <RouteElement>
                  <MyClient />
                </RouteElement>
              }
            />
          )}

          {/* Expert Documents */}
          {isDmsEnabled && (
            <Route
              path={`${getOrganizationViewUrl(organization.url, reactRoute.organization.documents.getMainRoute())}/*`}
              element={
                <RouteProtectedElement scopes={Scope.DmsFileRead}>
                  <ExpertDocuments />
                </RouteProtectedElement>
              }
            />
          )}

          {/* Client Documents */}
          {isDmsEnabled && (
            <Route
              path={`${getOrganizationViewUrl(
                organization.url,
                reactRoute.organization.clientDocuments.getMainRoute(),
              )}/*`}
              element={
                <RouteProtectedElement scopes={Scope.DmsFileRead}>
                  <ClientDocuments />
                </RouteProtectedElement>
              }
            />
          )}

          {/* Debtors */}
          {isSourceBilly && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.debtors.getMainRoute())}
              element={
                <RouteElement>
                  <DebtorsList />
                </RouteElement>
              }
            />
          )}

          {/* Creditors */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.creditors.getMainRoute())}
            element={
              <RouteProtectedElement scopes={Scope.BillRead}>
                {orgUsesReceiptsOnly ? <Navigate to={uploadsUrl} replace /> : <CreditorsList />}
              </RouteProtectedElement>
            }
          />

          {/* Suppliers Balance */}
          {isSuppliersBalanceEnabled && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.suppliersBalance.getDetailRoute())}
              element={
                <RouteElement>
                  <SuppliersBalanceDetailView />
                </RouteElement>
              }
            />
          )}

          {isSuppliersBalanceEnabled && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.suppliersBalance.getMainRoute())}
              element={
                <RouteElement>
                  <SuppliersBalanceOverview />
                </RouteElement>
              }
            />
          )}

          {/* Customers Balance */}
          {isCustomersBalanceEnabled && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.customersBalance.getDetailRoute())}
              element={
                <RouteElement>
                  <CustomersBalanceDetailView />
                </RouteElement>
              }
            />
          )}

          {isCustomersBalanceEnabled && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.customersBalance.getMainRoute())}
              element={
                <RouteElement>
                  <CustomersBalanceOverview />
                </RouteElement>
              }
            />
          )}

          {/* Expert Trial Balance */}
          {isTrialBalanceEnabled && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.expertTrialBalance.getMainRoute())}
              element={
                <RouteElement>
                  <ExpertTrialBalance />
                </RouteElement>
              }
            />
          )}

          {/* Expert Profit and Loss */}
          {isProfitAndLossEnabled && (
            <Route
              path={getOrganizationViewUrl(
                organization.url,
                reactRoute.organization.expertProfitAndLoss.getMainRoute(),
              )}
              element={
                <RouteElement>
                  <ExpertProfitAndLoss />
                </RouteElement>
              }
            />
          )}

          {/* Expert Balance Sheet */}
          {isBalanceSheetEnabled && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.expertBalanceSheet.getMainRoute())}
              element={
                <RouteElement>
                  <ExpertBalanceSheet />
                </RouteElement>
              }
            />
          )}

          {/* Expert Vat Declarations */}
          {isVatDeclarationsEnabled && (
            <Route
              path={getOrganizationViewUrl(
                organization.url,
                reactRoute.organization.expertVatDeclarations.getMainRoute(),
              )}
              element={
                <RouteElement>
                  <VatDeclarations />
                </RouteElement>
              }
            />
          )}

          {isVatDeclarationsEnabled && (
            <Route
              path={getOrganizationViewUrl(
                organization.url,
                reactRoute.organization.expertVatDeclarations.getEuSalesNoVatRoute(),
              )}
              element={
                <RouteElement>
                  <SalesTax />
                </RouteElement>
              }
            />
          )}

          {isVatDeclarationsEnabled && (
            <Route
              path={getOrganizationViewUrl(
                organization.url,
                reactRoute.organization.expertVatDeclarations.getViewVatDeclarationRoute(),
              )}
              element={
                <RouteElement>
                  <VatDeclaration />
                </RouteElement>
              }
            />
          )}

          {isVatDeclarationsEnabled && (
            <Route
              path={getOrganizationViewUrl(
                organization.url,
                reactRoute.organization.expertVatDeclarations.getSuggestionsMainRoute(),
              )}
              element={
                <RouteElement>
                  {isAuthorized(Scope.VatHelperUpsell) || isAuthorized(Scope.VatHelperWrite) ? (
                    <VatSuggestions />
                  ) : (
                    <RouteProtectedElement scopes={Scope.VatHelperUpsell}>
                      <VatSuggestions />
                    </RouteProtectedElement>
                  )}
                </RouteElement>
              }
            />
          )}

          {/* Clients */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.clients.getMainRoute())}
            element={
              <RouteProtectedElement scopes={Scope.ContactRead}>
                <ClientsList />
              </RouteProtectedElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.clients.getListEmptyRoute())}
            element={
              <RouteProtectedElement scopes={Scope.ContactRead}>
                <ClientsEmptyState />
              </RouteProtectedElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.clients.getViewClientRoute())}
            element={
              <RouteProtectedElement scopes={Scope.ContactRead}>
                <ContactDetail />
              </RouteProtectedElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.clients.getEditClientRoute())}
            element={
              <RouteProtectedElement scopes={Scope.ContactRead}>
                <ContactDetail isEditing />
              </RouteProtectedElement>
            }
          />

          {/* Suppliers */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.suppliers.getMainRoute())}
            element={
              <RouteProtectedElement scopes={Scope.ContactRead}>
                {orgUsesReceiptsOnly ? <Navigate to={uploadsUrl} replace /> : <SuppliersList />}
              </RouteProtectedElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.suppliers.getListEmptyRoute())}
            element={
              <RouteProtectedElement scopes={Scope.ContactRead}>
                {orgUsesReceiptsOnly ? <Navigate to={uploadsUrl} replace /> : <SuppliersEmptyState />}
              </RouteProtectedElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.suppliers.getSupplierDetailRoute())}
            element={<RouteProtectedElement scopes={Scope.ContactRead}>{supplierDetailElement}</RouteProtectedElement>}
          />

          {/* Find accountant */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.findAccountant.getMainRoute())}
            element={
              <RouteElement>
                <FindAccountant />
              </RouteElement>
            }
          />

          {/* Help Center */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.helpCenter.getMainRoute())}
            element={
              <RouteElement>
                <HelpCenterPage />
              </RouteElement>
            }
          />

          {/* Bills */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.bills.getSettingsRoute())}
            element={
              <RouteElement>
                <BillsSettings />
              </RouteElement>
            }
          />
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.bills.getMainRoute())}
            element={
              <RouteProtectedElement scopes={Scope.BillRead}>
                {orgUsesReceiptsOnly ? <Navigate to={uploadsUrl} replace /> : <BillsList />}
              </RouteProtectedElement>
            }
          />
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.bills.getListEmptyRoute())}
            element={
              <RouteProtectedElement scopes={Scope.BillRead}>
                {orgUsesReceiptsOnly ? <Navigate to={uploadsUrl} replace /> : <BillsListEmpty />}
              </RouteProtectedElement>
            }
          />

          {[
            reactRoute.organization.bills.getNewBillRoute(),
            reactRoute.organization.bills.getDuplicateRoute(),
            reactRoute.organization.bills.getEditBillRoute(),
            reactRoute.organization.bills.getEditBulkAttachmentsRoute(),
            reactRoute.organization.bills.getEditBulkRoute(),
            reactRoute.organization.bills.getCreateCreditNoteRoute(),
            reactRoute.organization.bills.getNewBillFromAttachmentRoute(),
            reactRoute.organization.bills.getNewBillFromContactRoute(),
          ]
            .map((route) => getOrganizationViewUrl(organization.url, route))
            .map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <RouteProtectedElement scopes={Scope.BillWrite}>
                    {orgUsesReceiptsOnly ? <Navigate to={uploadsUrl} replace /> : <BillEdit />}
                  </RouteProtectedElement>
                }
              />
            ))}

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.bills.getNewCreditNoteRoute())}
            element={
              <RouteProtectedElement scopes={Scope.BillWrite}>
                {orgUsesReceiptsOnly ? <Navigate to={uploadsUrl} replace /> : <BillEdit isNewCreditNote />}
              </RouteProtectedElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.bills.getViewBillRoute())}
            element={
              <RouteProtectedElement scopes={Scope.BillRead}>
                {orgUsesReceiptsOnly ? <Navigate to={uploadsUrl} replace /> : <BillView />}
              </RouteProtectedElement>
            }
          />

          {/* Bank */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.bank.getMainRoute())}
            element={
              <RouteProtectedElement scopes={Scope.BankReconciliationRead}>
                <BankConnectionEmpty />
              </RouteProtectedElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.bank.getNoBankAccountsRoute())}
            element={
              <RouteProtectedElement scopes={Scope.BankReconciliationRead}>
                <BankConnectionEmpty />
              </RouteProtectedElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.bank.getBankAccountsRoute())}
            element={
              <RouteProtectedElement scopes={Scope.BankReconciliationRead}>
                <BankAccounts />
              </RouteProtectedElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.bank.getBankTransactionsRoute())}
            element={
              <RouteProtectedElement scopes={Scope.BankReconciliationRead}>
                <BankTransactions />
              </RouteProtectedElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.cashpilot.getMainRoute())}
            element={
              <RouteProtectedElement scopes={Scope.CashFlowAnalyticsRead}>
                {accessToken ? <CashPilotView /> : null}
              </RouteProtectedElement>
            }
          />

          {/* Things to do  */}
          <Route
            path={`${getOrganizationViewUrl(organization.url, reactRoute.organization.thingsToDo.getMainRoute())}/*`}
            element={
              <RouteElement>
                <ThingsToDo />
              </RouteElement>
            }
          />

          {/* Onboarding */}
          <Route
            path={`${getOrganizationViewUrl(organization.url, reactRoute.organization.onboarding.getMainRoute())}/*`}
            element={
              <RouteElement>
                <OnboardingSetup />
              </RouteElement>
            }
          />

          {/* Quick Start */}
          {isEligibleForQuickStart && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.quickStart.getMainRoute())}
              element={
                <RouteElement>
                  <QuickStart />
                </RouteElement>
              }
            />
          )}

          {/* EInvoicing - currently under a temporary scope and hidden behind an internal feature flag */}
          <Route
            path={`${getOrganizationViewUrl(organization.url, reactRoute.organization.eInvoicing.getMainRoute())}/*`}
            element={
              <RouteProtectedElement scopes={Scope.EDocumentRecieveRegistration}>
                <EInvoicingSetup />
              </RouteProtectedElement>
            }
          />

          {/* Exports */}
          <Route
            path={`${getOrganizationViewUrl(organization.url, reactRoute.organization.exports.getMainRoute())}/*`}
            element={
              <RouteProtectedElement scopes={Scope.ExportRead}>
                <Exports />
              </RouteProtectedElement>
            }
          />

          {/* Uploads */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.uploads.getMainRoute())}
            element={
              <RouteElement>
                <ReceiptsList />
              </RouteElement>
            }
          />

          {/* Salary  */}
          <Route
            path={`${getOrganizationViewUrl(organization.url, reactRoute.organization.salary.getMainRoute())}/*`}
            element={
              <RouteElement>
                <SalaryIntegration />
              </RouteElement>
            }
          />

          {/* Products  */}
          {isSourceZervant && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.products.getMainRoute())}
              element={
                <RouteElement>
                  <Products />
                </RouteElement>
              }
            />
          )}

          {/* Delivery notes */}
          {isSourceZervant && hasDeliveryNotes && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.deliveryNotes.getMainRoute())}
              element={
                <RouteElement>
                  <DeliveryNotes />
                </RouteElement>
              }
            />
          )}

          {/* Debug mode */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.debug.getMainRoute())}
            element={
              <RouteElement>
                <Debug />
              </RouteElement>
            }
          />

          {/* Sales reports */}
          {isSourceZervant &&
            getSalesReportRoutePaths(organization.url).map((path) => (
              <Route
                key={path}
                path={path}
                element={<SalesReportRoute paths={getSalesReportRoutePaths(organization.url)} />}
              />
            ))}

          {isSourceZervant && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.salesReport.getListEmptyRoute())}
              element={
                <RouteProtectedElement scopes={Scope.ReportsInvoicing}>
                  <SalesReportEmpty />
                </RouteProtectedElement>
              }
            />
          )}

          {/* Vat declarations */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.vatDeclarations.getMainRoute())}
            element={
              <RouteElement>
                <VatDeclarations />
              </RouteElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(
              organization.url,
              reactRoute.organization.vatDeclarations.getEuSalesNoVatRoute(),
            )}
            element={
              <RouteElement>
                <SalesTax />
              </RouteElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(
              organization.url,
              reactRoute.organization.vatDeclarations.getViewVatDeclarationRoute(),
            )}
            element={
              <RouteElement>
                <VatDeclaration />
              </RouteElement>
            }
          />

          {/* VAT Suggestions: TaxHelper */}
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.vatSuggestions.getMainRoute())}
            element={
              <RouteElement>
                {isAuthorized(Scope.VatHelperUpsell) || isAuthorized(Scope.VatHelperWrite) ? (
                  <VatSuggestions />
                ) : (
                  <RouteProtectedElement scopes={Scope.VatHelperUpsell}>
                    <VatSuggestions />
                  </RouteProtectedElement>
                )}
              </RouteElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, 'review-entries')}
            element={
              <RouteElement>
                <Navigate
                  to={getOrganizationViewUrl(organization.url, reactRoute.organization.reviewEntries.getMainRoute())}
                  replace
                />
              </RouteElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.transactions.getMainRoute())}
            element={
              <RouteElement>
                {isFrenchUmbrellaOrganization ? (
                  <Transactions />
                ) : (
                  <RouteProtectedElement scopes={Scope.TransactionRead}>
                    <Transactions />
                  </RouteProtectedElement>
                )}
              </RouteElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.reviewEntries.getMainRoute())}
            element={
              <RouteElement>
                <ReviewEntries />
              </RouteElement>
            }
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.importedInvoices.getMainRoute())}
            element={
              <RouteElement>
                <ImportedInvoices />
              </RouteElement>
            }
          />

          {/* Reports */}
          <Route
            path={`${getOrganizationViewUrl(organization.url, reactRoute.organization.reports.getMainRoute())}/*`}
            element={
              <RouteProtectedElement scopes={Scope.ReportsRead}>
                <Reports />
              </RouteProtectedElement>
            }
          />

          {/* INVOICING DOCUMENTS */}

          {/*
            --== !!! IMPORTANT !!! ==--
            InvoiceEditor route MUST go before the other routes route due to the path param structure.
            For example the InvoiceView route can catch editor paths if placed before the InvoiceEditor route, preventing editor rendering.
          */}

          {/* Editor */}

          {isDemoInvoiceEditorEnabled && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.demoInvoice.getMainRoute())}
              element={
                <RouteElement>
                  <DemoInvoice />
                </RouteElement>
              }
            />
          )}

          {/* One <Route> per editor path, all pointing at the same InvoiceEditorRoute instance, so the
              recurring invoice toggle can navigate between them without remounting the editor - see
              InvoiceEditorRoute */}
          {isSourceZervant &&
            getInvoiceEditorRoutePaths(organization.url).map((path) => (
              <Route
                key={path}
                path={path}
                element={<InvoiceEditorRoute paths={getInvoiceEditorRoutePaths(organization.url)} />}
              />
            ))}

          {/* Invoices */}
          {/* NOTE: Keep Invoices always on the bottom of this file  */}
          {/* TODO: Solve issues with isSourceZervant condition */}
          {/* Document pages */}

          {isSourceZervant && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.invoices.getViewInvoiceRoute())}
              element={
                <RouteElement>
                  <InvoiceView />
                </RouteElement>
              }
            />
          )}

          {isSourceZervant && (
            <Route
              path={getOrganizationViewUrl(organization.url, reactRoute.organization.quotes.getViewQuoteRoute())}
              element={
                <RouteElement>
                  <QuoteView />
                </RouteElement>
              }
            />
          )}

          {isSourceZervant && (
            <Route
              path={getOrganizationViewUrl(
                organization.url,
                reactRoute.organization.recurringInvoices.getViewRecurringInvoiceRoute(),
              )}
              element={
                <RouteProtectedElement scopes={Scope.RecurringInvoiceRead}>
                  <RecurringInvoiceView />
                </RouteProtectedElement>
              }
            />
          )}

          {/* Document listings */}

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.invoices.getListEmptyRoute())}
            element={
              <RouteElement>
                <InvoicesListEmpty />
              </RouteElement>
            }
          />
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.invoices.getMainRoute())}
            element={<RouteElement>{isSourceZervant ? <ExternalInvoicesList /> : <InvoicesList />}</RouteElement>}
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.quotes.getListEmptyRoute())}
            element={
              <RouteElement>
                <QuotesListEmptyLegacy />
              </RouteElement>
            }
          />
          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.quotes.getMainRoute())}
            element={<RouteElement>{isSourceZervant ? <QuotesList /> : <QuotesListLegacy />}</RouteElement>}
          />

          <Route
            path={getOrganizationViewUrl(organization.url, reactRoute.organization.recurringInvoices.getMainRoute())}
            element={
              <RouteProtectedElement scopes={Scope.RecurringInvoiceRead}>
                <RecurringInvoicesList />
              </RouteProtectedElement>
            }
          />
        </Routes>
      </Styled.ContentArea>
    </Styled.DrawerBackgroundWrapper>
  )
}
